import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import type {
  OnboardingAnswerRequest,
  OnboardingProgress,
  OnboardingQuestionSummary,
  AIText,
} from '../types/onboarding';
import {
  getOnboardingAnswers,
  getOnboardingProgress,
  getOnboardingQuestion,
  submitOnboardingAnswer,
  updateOnboardingAnswer,
} from '../services/onboardingNewService';
import toast from 'react-hot-toast';
import { Toaster } from '../components/common';
import { useNavigate } from 'react-router-dom';
import { routes } from '../lib/routes';

interface OnboardingContextType {
  questions: OnboardingQuestionSummary[];
  selected: string;
  answer: string;
  isLoading: boolean;
  isQuestionLoading: boolean;
  isAnswerUpdating: boolean;
  progress: OnboardingProgress | null;
  aiText: AIText[];
  isEdit: boolean;
  updateAiText: (value: AIText) => void;
  setAnswer: (answer: string) => void;
  setSelected: (selected: string) => void;
  getQuestions: () => Promise<void>;
  updateAnswer: (
    questionId: string,
    user_response: string,
    aiText: string,
    hasNestedQuestion: boolean,
    questionIndex?: number,
    callback?: () => void,
    isEditQuestion?: boolean,
  ) => Promise<void>;
  submitAnswer: (data: OnboardingAnswerRequest) => Promise<void>;
  getSubmittedAnswers: (callback?: () => void) => Promise<void>;
  getProgress: () => Promise<void>;
  setQuestions: (questions: OnboardingQuestionSummary[]) => void;
  setIsQuestionLoading: (isQuestionLoading: boolean) => void;
  setIsAnswerUpdating: (isAnswerUpdating: boolean) => void;
  resetOnboarding: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  // Data state
  const [questions, setQuestions] = useState<OnboardingQuestionSummary[]>([]);
  const [aiText, setAiText] = useState<AIText[]>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [answer, setAnswer] = useState<string>('');
  const [selected, setSelected] = useState<string>('');

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [isAnswerUpdating, setIsAnswerUpdating] = useState(false);
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);

  const navigate = useNavigate();

  const getProgress = useCallback(async () => {
    try {
      const response = await getOnboardingProgress();
      setProgress(response.result);
    } catch (error) {
      console.error('Get onboarding progress failed:', error);
    }
  }, []);

  const updateAiText = useCallback((value: AIText) => {
    setAiText(prev => {
      const exists = prev.some(
        item => item.question_id === value.question_id
      );

      if (exists) return prev;

      return [...prev, value];
    });
  }, []);


  const saveAiText = useCallback((questions: OnboardingQuestionSummary[]) => {
    const result = questions.map(question => {
      return {
        question_id: question.id,
        ai_text: question.ai_text,
      }
    });
    setAiText(result);
  }, []);

  const getQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOnboardingQuestion();
      if (response.code !== 200 || !response.result) {
        toast.custom(t => (
          <Toaster message={response.message || 'Failed to load questions'} type='error' id={t.id} />
        ));
        return;
      }

      if (questions.length === 0) {
        setQuestions([response.result]);
        saveAiText([response.result]);
      } else {
        setQuestions(prev => [...prev, response.result]);
        saveAiText([...questions, response.result]);
      }
    } catch (error) {
      console.error('Get onboarding questions failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [questions.length]);

  const getSubmittedAnswers = useCallback(async (callback?: () => void) => {
    setIsLoading(true);
    try {
      const response = await getOnboardingAnswers();
      if (response.code !== 200) {
        toast.custom(t => (
          <Toaster message={response.message} type='error' id={t.id} />
        ));
        return;
      }

      if (response.result.length > 0) {
        const responseQuestion = await getOnboardingQuestion();

        if (responseQuestion.code !== 200) {
          toast.custom(t => (
            <Toaster
              message={responseQuestion.message}
              type='error'
              id={t.id}
            />
          ));
          return;
        }

        if (responseQuestion.result) {
          setQuestions([...response.result, responseQuestion.result]);
          saveAiText([...response.result, responseQuestion.result]);
          if (callback) {
            callback();
          }
          return;
        }

        setQuestions(response.result);
        saveAiText(response.result);

        if (callback) {
          callback();
        }
      }
    } catch (error) {
      console.error('Get onboarding answers failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [saveAiText]);

  const submitAnswer = useCallback(async (data: OnboardingAnswerRequest) => {
    setIsQuestionLoading(true);
    const startTime = Date.now(); // Record start time

    try {
      // Add response to the previous question

      setQuestions(prev => {
        if (prev.length === 0) return prev;

        const updatedLastQuestion = {
          ...prev[prev.length - 1],
          user_input_response: data.user_response,
          user_response: data.user_response,
          answer_id: Date.now().toString(),
        };

        return [
          ...prev.slice(0, -1),
          updatedLastQuestion,
        ];
      });

      const response = await submitOnboardingAnswer(data);

      // Calculate elapsed time and ensure minimum 2 seconds
      const elapsedTime = Date.now() - startTime;
      const minDelay = 2000; // 2 seconds in milliseconds
      const remainingTime = Math.max(0, minDelay - elapsedTime);

      // Wait for remaining time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.code !== 200) {
        toast.custom(t => (
          <Toaster message={response.message} type='error' id={t.id} />
        ));
        setIsQuestionLoading(false);
        return;
      }

      if (response.result.nextQuestion) {
        // Update response to the previous question and add the next question

        setQuestions(prev => {
          if (prev.length === 0) return prev;

          const updatedLastQuestion = {
            ...prev[prev.length - 1],
            user_input_response: response.result.submitResponse.user_input_response,
            user_response: response.result.submitResponse.user_response,
            answer_id: response.result.submitResponse.answer_id,
            is_answer_accepted: response.result.submitResponse.is_answer_accepted,
          };

          return [
            ...prev.slice(0, -1),
            updatedLastQuestion,
            response.result.nextQuestion,
          ];
        });

        updateAiText({
          question_id: response.result.nextQuestion.id,
          ai_text: response.result.nextQuestion.ai_text,
        });
      } else {
        navigate(routes.onboardingSummary);
      }
    } catch (error) {
      // Handle error case - still respect minimum delay
      const elapsedTime = Date.now() - startTime;
      const minDelay = 2000;
      const remainingTime = Math.max(0, minDelay - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      console.error('Submit onboarding answer failed:', error);
    } finally {
      setIsQuestionLoading(false);
    }
  }, [navigate, updateAiText]);

  const updateAnswer = useCallback(
    async (
      questionId: string,
      user_response: string,
      aiText: string,
      hasNestedQuestion = false,
      questionIndex?: number,
      callback?: () => void,
      isEditQuestion?: boolean,
    ) => {
      setIsEdit(false);
      setIsAnswerUpdating(true);

      if (isEditQuestion) {
        setIsQuestionLoading(true);

        setQuestions(prev =>
          prev.map((question, index) =>
            question.id === questionId && index === questionIndex
              ? { ...question, user_response, user_input_response: user_response }
              : question
          )
        );
      }

      const startTime = Date.now(); // Record start time

      try {
        const response = await updateOnboardingAnswer({
          question_id: questionId,
          user_response,
          ai_text: aiText,
        });

        let responseAnswers = null;
        if (hasNestedQuestion && response.code === 200 && response.result.submitResponse.is_answer_accepted) {
          responseAnswers = await getOnboardingAnswers();
        }

        // Calculate elapsed time after all API calls and ensure minimum 2 seconds
        const elapsedTime = Date.now() - startTime;
        const minDelay = 2000; // 2 seconds in milliseconds
        const remainingTime = Math.max(0, minDelay - elapsedTime);

        // Wait for remaining time if needed
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        // Handle errors after delay
        if (response.code !== 200) {
          toast.custom(t => (
            <Toaster message={response.message} type='error' id={t.id} />
          ));
          setIsAnswerUpdating(false);
          setIsQuestionLoading(false);
          return;
        }

        if (hasNestedQuestion) {
          if (responseAnswers && responseAnswers.code !== 200) {
            toast.custom(t => (
              <Toaster
                message={responseAnswers.message}
                type='error'
                id={t.id}
              />
            ));
            setIsAnswerUpdating(false);
            setIsQuestionLoading(false);
            return;
          }

          if (!response.result.submitResponse.is_answer_accepted) {
            setIsEdit(true);
            setQuestions(prev => [
              ...prev.map(question =>
                question.id === questionId
                  ? { ...question, user_response, user_input_response: user_response }
                  : question
              ),
              response.result.nextQuestion,
            ]);
          }

          if (responseAnswers && responseAnswers.result.length > 0 && response.result.submitResponse.is_answer_accepted) {

            await getProgress();

            setQuestions([
              ...responseAnswers.result,
              response.result.nextQuestion,
            ]);
          }
        } else {
          if (!response.result.submitResponse.is_answer_accepted) {
            setIsEdit(true);
            setQuestions(prev => {
              const updatedQuestions = prev.map((question, index) =>
                question.id === questionId && index === questionIndex
                  ? { ...question, user_response, user_input_response: user_response, is_answer_accepted: response.result.submitResponse.is_answer_accepted }
                  : question
              );

              const lastQuestion = updatedQuestions[updatedQuestions.length - 1];
              const hasPendingQuestion = lastQuestion && !lastQuestion.user_response && !lastQuestion.is_answer_accepted;

              if (hasPendingQuestion && !isEditQuestion) {
                return [...updatedQuestions.slice(0, -1), response.result.nextQuestion];
              } else {
                return [...updatedQuestions, response.result.nextQuestion];
              }
            });
          } else {
            if (isEditQuestion) {
              setQuestions(prev =>
                [...prev.map((question, index) =>
                  question.id === questionId && index === questionIndex
                    ? { ...question, user_response, user_input_response: user_response, is_answer_accepted: response.result.submitResponse.is_answer_accepted }
                    : question
                )
                  , response.result.nextQuestion]
              );
            } else {
              setQuestions(prev =>
                prev.map((question, index) =>
                  question.id === questionId && index === questionIndex
                    ? { ...question, user_response, user_input_response: user_response, is_answer_accepted: response.result.submitResponse.is_answer_accepted }
                    : question
                )
              );
            }
          }
        }

        if (!response.result.nextQuestion) {
          navigate(routes.onboardingSummary);
        }

        setSelected('');
        setAnswer('');
        setIsAnswerUpdating(false);
        setIsQuestionLoading(false);
        if (callback) {
          callback();
        }
      } catch (error) {
        // Calculate elapsed time and ensure minimum 2 seconds
        const elapsedTime = Date.now() - startTime;
        const minDelay = 2000;
        const remainingTime = Math.max(0, minDelay - elapsedTime);

        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        console.error('Update onboarding answer failed:', error);
        setIsAnswerUpdating(false);
      }
    },
    [navigate, getProgress]
  );

  const resetOnboarding = useCallback(async () => {
    setQuestions([]);
    setAnswer('');
    setSelected('');
    setIsQuestionLoading(false);
    setIsAnswerUpdating(false);
    setProgress(null);
  }, []);

  const value = useMemo(
    () => ({
      questions,
      selected,
      answer,
      isLoading,
      isQuestionLoading,
      isAnswerUpdating,
      progress,
      aiText,
      isEdit,
      updateAiText,
      setSelected,
      setAnswer,
      getSubmittedAnswers,
      getQuestions,
      updateAnswer,
      submitAnswer,
      getProgress,
      setQuestions,
      setIsQuestionLoading,
      setIsAnswerUpdating,
      resetOnboarding,
    }),
    [
      questions,
      selected,
      answer,
      isLoading,
      isQuestionLoading,
      progress,
      isAnswerUpdating,
      aiText,
      isEdit,
      updateAiText,
      getQuestions,
      setSelected,
      getSubmittedAnswers,
      setAnswer,
      updateAnswer,
      submitAnswer,
      getProgress,
      setQuestions,
      setIsQuestionLoading,
      setIsAnswerUpdating,
      resetOnboarding,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);

  if (context === undefined) {
    throw new Error(
      'useOnboarding must be used within an OnboardingProvider'
    );
  }

  return context;
};

export default OnboardingContext;
